<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, nextTick, watch } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useEvents } from '@/composables/useEvents'
import { useFilters } from '@/composables/useFilters'
import TimelineItem from './TimelineItem.vue'
import { formatDateTime } from '@/utils'
import type { Entry } from '@/types'

const emit = defineEmits<{
  (e: 'edit-entry', entry: Entry): void
  (e: 'publish-entry', entryId: string): void
  (e: 'delete-entry', entryId: string): void
}>()

const { t } = useI18n()
const { isLoading } = useEvents()
const { filteredEntries } = useFilters()

const openEntryIds = ref(new Set<string>())
const lastHashScrolled = ref('')

// Determine where to insert the "now" marker
const todayMarkerIndex = computed(() => {
  const now = new Date()
  for (let i = 0; i < filteredEntries.value.length; i++) {
    const entry = filteredEntries.value[i]
    if (entry.start_at) {
      const entryDate = new Date(entry.start_at)
      if (entryDate < now) return i
    }
  }
  return -1
})

function todayLabel() {
  return formatDateTime(new Date())
}

function toggleDetails(id: string, open: boolean) {
  if (open) {
    openEntryIds.value.add(id)
  } else {
    openEntryIds.value.delete(id)
  }
}

function isDetailsOpen(id: string): boolean {
  return openEntryIds.value.has(id)
}

// Handle deep linking
onMounted(() => {
  void handleHash()
  window.addEventListener('hashchange', handleHash)
})

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHash)
})

watch(filteredEntries, () => {
  const hash = window.location.hash
  if (!hash || !hash.startsWith('#/')) return
  if (hash === lastHashScrolled.value) return
  void handleHash()
})

async function handleHash() {
  const hash = window.location.hash
  if (!hash || !hash.startsWith('#/')) return
  const entryId = hash.slice(2)
  if (!entryId) return
  openEntryIds.value.add(entryId)

  await nextTick()

  const el = document.getElementById(entryId)
  if (!el) return

  const header = document.querySelector('.header') as HTMLElement | null
  const headerOffset = header ? header.offsetHeight + 20 : 150
  const elementPosition = el.getBoundingClientRect().top + window.scrollY
  window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' })
  lastHashScrolled.value = hash
}

</script>

<template>
  <main class="main">
    <div class="timeline">
      <!-- Loading state -->
      <div v-if="isLoading && filteredEntries.length === 0" class="loading">
        {{ t('loading') }}
      </div>

      <!-- Empty state -->
      <div v-else-if="filteredEntries.length === 0" class="empty-state">
        <p>{{ t('no-results') }}</p>
      </div>

      <!-- Timeline entries -->
      <template v-else>
        <template v-for="(entry, i) in filteredEntries" :key="entry.id">
          <!-- Today marker -->
          <div v-if="i === todayMarkerIndex" id="today-marker" class="timeline-item today-marker">
            <div class="timeline-marker today-dot"></div>
            <div class="today-line"></div>
            <div class="today-label">{{ todayLabel() }}</div>
          </div>

          <TimelineItem
            :entry="entry"
            :details-open="isDetailsOpen(entry.id)"
            @toggle-details="toggleDetails"
            @edit-entry="(entry: Entry) => emit('edit-entry', entry)"
            @publish-entry="(id: string) => emit('publish-entry', id)"
            @delete-entry="(id: string) => emit('delete-entry', id)"
          />
        </template>
      </template>
    </div>
  </main>
</template>
